// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Tester.ts
//
// An applet for delivering a test on various topics.
//
import type { Accommodation } from 'shared/definitions/user-object-definitions';
import type { UPracticeData } from 'definitions/progress-data';
import type {
  CheckpointResults,
  LessonCheckpointTest,
  MergedCustomCheckpoint,
  Passing,
  SavedCustomCheckpoint,
  WeightedAnswerAndData,
} from 'shared/definitions/checkpoint-definitions';
import type {
  CheckpointStats,
  CheckpointSection,
  CheckpointTest,
  CheckpointOptions,
  CheckpointSectionOptions,
  QuestionGroup,
  QuestionType,
  QuestionBank,
} from 'shared/definitions/checkpoint-definitions';
import type { AnswerAndData } from '../uTheory.Interaction/interactiveQuestionPrototype';
import utils from '../utils';
import CountdownTimer from './countdownTimer';
import * as Interaction from '../interaction';
import * as Skills from '../uTheory.Skills/skills';
import type { UserData } from '../uTheory.Interaction/interactivePrototype';
import type InteractivePrototype from '../uTheory.Interaction/interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import errorProvider from '../errorProvider';

type QuestionOverCallback = (data: {
  answerAndData: WeightedAnswerAndData;
  stats: CheckpointStats;
}) => any;

const debug = false;
const log = debug ? (...args: any[]) => console.log(...args) : (): void => {};

interface Section {
  options: CheckpointSectionOptions;
  timer?: CountdownTimer;
  questions: Question[];
}

interface Question {
  type: string;
  /** unique identified for sorting questions w/ same interactive type but different options */
  typeUuid: string;
  weight: number;
  options: Record<string, any>;
  question: any;
}

type ClassSectionPassingPercent = false | number;

export class Tester {
  private userData: UserData;

  private test: {
    options: CheckpointOptions;
    sections: Section[];
  };

  private testObject: CheckpointTest | LessonCheckpointTest;

  private gameOverCallbacks: ((results?: CheckpointResults) => void)[] = [];

  private questionOverCallbacks: QuestionOverCallback[] = [];

  private skillDataObjects: UPracticeData[] = [];

  private questionStats: WeightedAnswerAndData[] = [];

  private stats: CheckpointStats;

  private questionNumber = 0;

  private sectionNumber = 0;

  private totalQuestions = 0;

  private sectionQuestionNumber = 0;

  private totalSectionQuestions = 0;

  private previousQuestion: Question;

  private questionStartTime = 0;

  private testTimer?: CountdownTimer;

  private interactive: InteractivePrototype;

  /** resolves when the interactive has loaded & loaded the question */
  private questionPromise: Promise<any>;

  private displayDiv: HTMLDivElement;

  private testTimerDiv: HTMLDivElement;

  private totalQuestionsDiv: HTMLDivElement;

  private sectionTimerDiv: HTMLDivElement;

  private sectionQuestionsDiv: HTMLDivElement;

  private infoDiv: HTMLDivElement;

  private questionDiv: HTMLDivElement;

  private statsDiv: HTMLDivElement;

  private classSectionPassingPercent: ClassSectionPassingPercent = false;

  private checkpointAttemptId: string;

  /** Set once destroy() has run; makes timer/section callbacks no-ops. */
  private destroyed = false;

  // Public constructor
  constructor({
    displayDiv,
    testObject,
    checkpointAttemptId,
    userData,
    classSectionPassingPercent,
  }: {
    displayDiv: HTMLDivElement;
    testObject: CheckpointTest | LessonCheckpointTest;
    checkpointAttemptId: string;
    userData: UserData;
    classSectionPassingPercent?: ClassSectionPassingPercent;
  }) {
    log('tester sectionPassingPercent', classSectionPassingPercent);
    this.checkpointAttemptId = checkpointAttemptId;
    this.classSectionPassingPercent = classSectionPassingPercent || false;
    if (window) (window as any).tester = this;
    this.testObject = testObject;
    this.userData = userData;

    this.test = processTest(testObject);
    this.totalQuestions = this.test.sections.reduce(
      (totalQuestions, section) => totalQuestions + section.questions.length,
      0,
    );
    this.replaceGlobalQuestionBankHandlebars(this.test);
    this.initializeDisplay(displayDiv);
    log('initializing stats from testObject:');
    log(testObject);
    this.stats = {
      checkpointName: testObject.options.name,
      customCheckpoint: testObject.customCheckpoint || false,
      sections: [],
      overall: {
        rightAnswers: 0,
        wrongAnswers: 0,
        weightedRightAnswers: 0,
        weightedWrongAnswers: 0,
        weightedTotalQuestions: 0,
        percentage: 0,
        totalQuestions: 0,
      },
      uuid: 0,
    };
    if (testObject.passing)
      this.stats.passing = getPassingWithAccommodation(
        testObject.passing,
        this.userData?.accommodation,
      );
    log(this.stats);
  }

  /**
   * This is run after the test is processed, so that the distribution
   * of the various {{ handleBar }} questions will be even.
   * @param test
   */
  private replaceGlobalQuestionBankHandlebars(test: {
    options: CheckpointOptions;
    sections: Section[];
  }): void {
    // grab all section questions into a flat array
    const questions = test.sections.map((s) => s.questions).reduce((a, b) => a.concat(b), []);
    // log('questions', utils.clone(questions.map(q => q.question), true));

    // figure out how many of each question group are needed
    const reGlobal = /{{ ?([^}]*) ?}}/gim;
    const handlebarsTally = questions.reduce(
      (handleBarGroups: { [groupName: string]: number }, questionObj) => {
        function tally(qString: any): void {
          if (typeof qString !== 'string') return;
          (qString.match(reGlobal) || [])
            .map((match) => match.replace(reGlobal, '$1'))
            .forEach((match) => {
              if (handleBarGroups[match]) handleBarGroups[match]++;
              else handleBarGroups[match] = 1;
            });
        }
        if (typeof questionObj.question === 'string') tally(questionObj.question);
        else utils.tree(questionObj.question, tally, true);
        return handleBarGroups;
      },
      {},
    );
    // log('handlebarsTally', handlebarsTally);

    // then assign values
    const questionGenerator = new QuestionGenerator();
    const handlebarsQuestions = Object.keys(handlebarsTally).reduce(
      (handlebarQuestionsDictionary: { [handlebarGroupName: string]: any[] }, needle) => {
        const total = handlebarsTally[needle];
        const possibleQuestions = this.testObject.globalQuestionBanks?.[needle];
        if (!possibleQuestions) throw new Error(`${needle} not found in globalQuestionBanks`);
        // log(`Getting ${total} {{${needle}}} questions`);
        handlebarQuestionsDictionary[needle] =
          possibleQuestions.length > 1
            ? questionGenerator.rejectDuplicates(possibleQuestions, total)
            : possibleQuestions;
        // log(`Got ${handlebarsQuestions[needle].join(', ')}`);
        return handlebarQuestionsDictionary;
      },
      {},
    );
    // log('handlebarsQuestions', utils.clone(handlebarsQuestions, true));

    function replace(qString: any): any {
      if (typeof qString !== 'string') return qString;
      (qString.match(reGlobal) || [])
        .map((match) => match.replace(reGlobal, '$1'))
        .forEach((match) => {
          const reSingle = new RegExp(`{{ ?${match} ?}}`, 'im');
          qString = qString.replace(reSingle, handlebarsQuestions[match].pop());
        });
      return qString;
    }

    function transform(value, key, sourceObj): void {
      if (typeof value !== 'string') return;
      sourceObj[key] = replace(value);
    }

    questions.forEach((questionObj) => {
      if (typeof questionObj.question === 'string') {
        questionObj.question = replace(questionObj.question);
        return;
      }

      // Clone it since this same object may be used by other questions
      questionObj.question = utils.clone(questionObj.question, true);
      // It's an object or array -- go deep on it, transforming as needed.
      utils.tree(questionObj.question, transform, true);
    });
    // log('handlebarsQuestions after use', handlebarsQuestions);
    // log('section questions', questions.map(q => q.question));
    // re = /{{ ?([^}]*) ?}}/igm
    // ('{{cats}} and {{ dogs }} and {{{oranges}}}'.match(re) || []).map(m => m.replace(re, '$1'));
  }

  static processTest(test: CheckpointTest): {
    options: CheckpointOptions;
    sections: Section[];
  } {
    return processTest(test);
  }

  static processSection(section: CheckpointSection): Question[] {
    return processSection(section);
  }

  private initializeDisplay(displayDiv: HTMLDivElement): void {
    this.displayDiv = displayDiv;
    // Setup the display:
    // Create statsDiv:
    this.statsDiv = this.createDiv({ display: 'none' });

    // Prepare test timer, if one exists.
    if (
      typeof this.testObject.options.timeLimit !== 'undefined' &&
      this.testObject.options.timeLimit !== null
    ) {
      this.testTimerDiv = this.createDiv({ float: 'left' }, this.statsDiv);
      this.testTimerDiv.innerHTML = 'Total Time Remaining: <span id="testTimer"></span>';
      const testTimerSpan = document.getElementById('testTimer');
      this.testTimer = new CountdownTimer(testTimerSpan);
      this.testTimer.set(this.testObject.options.timeLimit, this.userData?.accommodation?.time);
    }
    this.totalQuestionsDiv = this.createDiv({ float: 'right' }, this.statsDiv);
    this.totalQuestionsDiv.innerHTML = `Question 1 of ${this.totalQuestions}`;
    this.sectionTimerDiv = this.createDiv({ float: 'left', clear: 'left' }, this.statsDiv);
    this.sectionQuestionsDiv = this.createDiv({ float: 'right', clear: 'right' }, this.statsDiv);

    // Create infoDiv:
    this.infoDiv = this.createDiv({ clear: 'both' });
    this.infoDiv.innerHTML = `<h2>${this.testObject.options.name}<h2>`;
    if (this.testObject.debugMode) {
      this.infoDiv.innerHTML += '<h1>WARNING: Debug Mode -- every question will be delivered.</h1>';
    }
    if (typeof this.testObject.options.instructions !== 'undefined') {
      this.infoDiv.innerHTML += Tester.processTestInstructions(
        this.testObject,
        this.test,
        this.userData.accommodation,
        this.classSectionPassingPercent,
      );
    }
    const buttonText = this.testObject.customCheckpoint ? 'Start Test' : 'Start Checkpoint';
    const button = Tester.createButton(buttonText, this.startTest.bind(this));
    this.infoDiv.appendChild(button);

    // Create questionDiv:
    this.questionDiv = this.createDiv({
      display: 'none',
      clear: 'both',
    });

    // Create instructions div & show any full test instructions
  }

  static getTestPassingSentence(
    test: CheckpointTest | LessonCheckpointTest | MergedCustomCheckpoint,
    accuracyAccommodationCoefficient: number = 1,
    classSectionPassingPercent?: ClassSectionPassingPercent,
  ): string {
    if (!classSectionPassingPercent && (!test.passing || !test.passing.type)) return '';
    const accommodationSentence =
      accuracyAccommodationCoefficient !== 1
        ? ` This includes a ${accuracyAccommodationCoefficient}x accuracy accommodation.`
        : '';

    if (test.passing?.type === 'overall') {
      const passingPercent = (
        test.passing?.passingPercent * accuracyAccommodationCoefficient
      ).toFixed(0);
      return `A score of ${passingPercent}% or higher is required to pass.${accommodationSentence}`;
    }
    if (test.passing?.type === 'sections') {
      const sharedPassingPercentForEachSection = Tester.getSectionPassingPercents(test);
      if (sharedPassingPercentForEachSection === false) {
        return `You must pass each section of this test to pass. (Required passing scores for each
          section will be displayed with each section's instructions.)`;
      }
      const sectionPassingPercents = (
        sharedPassingPercentForEachSection * accuracyAccommodationCoefficient
      ).toFixed(0);
      return sectionPassingPercents
        ? `A score of ${sectionPassingPercents}% or higher on each section is required to pass.${accommodationSentence}`
        : // this should never happen
          'You must pass each section of this test to pass.';
    }
    if (test.passing?.type === 'both') {
      const sharedPassingPercentForEachSection = Tester.getSectionPassingPercents(test);

      const sectionPassingPercent = (
        (sharedPassingPercentForEachSection as number) * accuracyAccommodationCoefficient
      ).toFixed(0);

      const passingPercent = (
        test.passing?.passingPercent * accuracyAccommodationCoefficient
      ).toFixed(0);
      // If there's no shared section passing percent...
      if (sharedPassingPercentForEachSection === false) {
        return `An overall score of ${passingPercent} is required to pass this test, in addition each section must be passed.
        (Required passing scores for each section vary, and will be displayed with each section's instructions.)`;
      }

      return sectionPassingPercent
        ? `You must receive a score of ${sectionPassingPercent}% or higher on each section to pass, ` +
            `as well as an overall score of ${passingPercent}% or higher.${accommodationSentence}`
        : `You must pass each section of this test and recieve an overall score of ${passingPercent} or higher to pass.${accommodationSentence}`;
    }

    if (classSectionPassingPercent) {
      const passingPercent = (
        classSectionPassingPercent * accuracyAccommodationCoefficient
      ).toFixed(0);
      return `A score of ${passingPercent}% or higher is required to pass.${accommodationSentence}`;
    }
    log('this should never happen -- test.passing:', test.passing);
    return '';
  }

  static getSectionPassingSentence(
    test: CheckpointTest | LessonCheckpointTest,
    sectionNumber: number,
    accuracyAccommodationCoefficient = 1,
  ): string {
    // If there's a shared, overall section passing percent, we don't need to display it with each section.
    // It was likely displayed at the start of the test.
    if (Tester.getSectionPassingPercents(test) !== false) return '';

    const sectionPassingPercent = Tester.getSectionPassingPercents(test, sectionNumber);
    const accommodationSentence =
      accuracyAccommodationCoefficient !== 1
        ? ` This includes a ${accuracyAccommodationCoefficient}x accuracy accommodation.`
        : '';

    return sectionPassingPercent === false
      ? ''
      : `A score of ${sectionPassingPercent}% or higher is required to pass this section.${accommodationSentence}`;
  }

  /**
   * Returns the section passing percent for the test as a whole, or a specific section if specified.
   * If a single sectionPassingPercent for the test as a whole cannot be determined, or if passing each
   * section is not required, false is returned.
   * @param test
   * @param sectionNumber
   */
  static getSectionPassingPercents(
    test: CheckpointTest | LessonCheckpointTest | MergedCustomCheckpoint | SavedCustomCheckpoint,
    sectionNumber?: number,
  ): false | number {
    if (!test.passing) return false;
    if (test.passing.type === 'overall') {
      return false;
    }
    const passingPercent = Number(test.passing.passingPercent) || false;

    if (typeof sectionNumber !== 'undefined') {
      const sectionOptions = test.sections[sectionNumber].options;
      return typeof sectionOptions.passingPercent === 'undefined' ||
        sectionOptions.passingPercent === null
        ? passingPercent
        : Number(sectionOptions.passingPercent);
    }

    return test.sections
      .filter((s) => !s.options.disabled)
      .reduce((accum: any, section) => {
        const sectionPassingPercent =
          typeof section.options.passingPercent === 'undefined' ||
          section.options.passingPercent === null
            ? passingPercent
            : Number(section.options.passingPercent);
        if (accum === false) return false;
        if (accum === undefined) return sectionPassingPercent;
        return sectionPassingPercent === accum ? sectionPassingPercent : false;
      }, undefined);
  }

  /**
   * Replace handlebar variables in instructions
   * {{testName}} set by test.options.name
   * {{totalTime}} by test time or total of section times.
   * {{secondsPerQuestion}}
   * {{sections}} set by number of enabled sections
   * {{totalQuestions}}
   * {{passingPercent}} set by test.passing.passingPercent
   * {{passingSentence}} A human-readable sentence about passing requirements.
   * {{attemptsAllowed}} number of attempts allowed
   * {{attemptsAllowedSentence}} A human-readable sentence about how many times the test may be taken.
   * {{attemptToUse}} 'most recent' or 'highest scoring'
   * {{attemptToUseSentence}} 'Your most recent attempt will replace any other attempts.' or '' if not repeateable
   * @param instructions
   */
  static processTestInstructions(
    test: CheckpointTest | LessonCheckpointTest | MergedCustomCheckpoint,
    processedTest?: {
      options: CheckpointOptions;
      sections: Section[];
    },
    accommodation: Pick<Accommodation, 'time' | 'accuracy'> = { time: 1, accuracy: 1 },
    sectionPassingPercent?: ClassSectionPassingPercent,
  ): string {
    processedTest = processedTest || processTest(test);
    const totalQuestions = processedTest.sections.reduce(
      (totalQ, section) => (section.questions.length || 0) + totalQ,
      0,
    );

    const timeAccommodationCoefficient = accommodation.time || 1;
    const accuracyAccommodationCoefficient =
      'accuracy' in accommodation ? accommodation.accuracy : 1;

    let time:
      | undefined
      | {
          minutes: number;
          seconds: number;
          totalTimeSeconds: number;
          humanString: string;
          clockString: string;
        };

    if (test.options.timeLimit) {
      time = CountdownTimer.processTimeString(
        processedTest.options.timeLimit,
        timeAccommodationCoefficient,
      );
    } else {
      const totalSecondsFromSections = processedTest.sections
        .filter((s) => !s.options.disabled && s.questions.length)
        .map((s) =>
          s.options.timeLimit
            ? CountdownTimer.processTimeString(s.options.timeLimit).totalTimeSeconds
            : NaN,
        )
        .reduce((accum, sectionSeconds) => accum + sectionSeconds, 0);
      if (!Number.isNaN(Number(totalSecondsFromSections))) {
        const timeString = `${~~(totalSecondsFromSections / 60)}:${totalSecondsFromSections % 60}`;
        time = CountdownTimer.processTimeString(timeString, timeAccommodationCoefficient);
      }
    }

    const attemptsAllowed: string | number =
      test.allowMultipleAttempts === 0
        ? '1'
        : test.allowMultipleAttempts === -1
        ? 'unlimited'
        : test.allowMultipleAttempts || '1';
    const passingSentence = Tester.getTestPassingSentence(
      test,
      accuracyAccommodationCoefficient,
      sectionPassingPercent,
    );
    const replaceables = {
      testName: test.options.name,
      totalTime: time ? time.humanString : 'n/a',
      secondsPerQuestion: time ? ~~(time.totalTimeSeconds / totalQuestions) : 'n/a',
      sections: processedTest.sections.filter((s) => s.questions.length).length,
      totalQuestions,
      passingPercent:
        test.passing && test.passing.passingPercent ? test.passing.passingPercent : 'n/a',
      passingSentence,
      attemptsAllowed,
      attemptsAllowedSentence: ((): string => {
        const attempts = test.allowMultipleAttempts;
        if (attempts === -1) return 'You may retake this test as many times as needed.';
        if (!attempts || attempts === 1) return 'This test may only be taken one time.';
        return `This test may be taken up to ${attempts} times.`;
      })(),
      attemptToUse: test.attemptToUse === 'mostRecent' ? 'most recent' : 'highest scoring',
      attemptToUseSentence:
        test.allowMultipleAttempts !== 1 && test.allowMultipleAttempts !== 0
          ? `Your ${test.attemptToUse === 'mostRecent' ? 'most recent' : 'highest scoring'}
        attempt will replace any other attempts.`
          : '',
    };

    let instructions = test.options.instructions;
    if (passingSentence) {
      instructions = instructions.replace('{{passingSentence}}', '{{passingSentence}} ');
    }
    if (test.allowMultipleAttempts !== 1 && test.allowMultipleAttempts !== 0) {
      instructions = instructions.replace('{{attemptToUseSentence}}', '{{attemptToUseSentence}} ');
    }
    instructions = instructions.replace(
      '{{attemptsAllowedSentence}}',
      '{{attemptsAllowedSentence}} ',
    );
    Object.keys(replaceables).forEach((needle) => {
      const re = new RegExp(`{{${needle}}}`, 'g');
      instructions = instructions.replace(re, replaceables[needle]);
    });
    return instructions;
  }

  /**
   * Replace handlebar variables in instructions
   * {{testName}} set by test.options.name
   * {{sectionName}} set by section.options.name
   * {{sectionQuestions}} number of questions in this section
   * {{sectionPassingPercent}} set by test.section.options.passingPercent or test.passing.passingPercent
   * {{sectionTime}} time limit in MM:SS for this section
   * {{secondsPerQuestion}}
   * {{sections}} set by number of enabled sections
   * {{sectionNumber}} the number of the current section
   * {{totalQuestions}}
   * {{passingPercent}} set by test.passing.passingPercent
   * {{passingSentence}} A human-readable sentence about passing requirements for the section.
   * @param sectionNumber
   */
  private processSectionInstructions(section: Section, instructions: string): string {
    const time = section.options.timeLimit
      ? CountdownTimer.processTimeString(
          section.options.timeLimit,
          this.userData.accommodation.time || 1,
        )
      : false;

    const hasMultipleSections =
      this.test.sections.filter((s) => s.questions.length > 0 && !s.options.disabled).length > 1;
    const sectionNumber = this.test.sections.filter(
      (s, i, sections) => s.questions.length && i <= sections.indexOf(section),
    ).length;
    const sectionName = section.options.name;

    const replaceables = {
      testName: this.testObject.options.name,
      sectionHeader: hasMultipleSections
        ? `<h2>Section ${sectionNumber}: ${sectionName}</h2>`
        : `<h2>${sectionName}</h2>`,
      sectionName,
      sectionQuestions: section.questions.length,
      sectionPassingPercent:
        typeof section.options.passingPercent !== 'undefined'
          ? section.options.passingPercent
          : 'n/a',
      sectionTime: time ? time.humanString : '',
      secondsPerQuestion: time ? ~~(time.totalTimeSeconds / section.questions.length) : 'n/a',
      sections: this.test.sections.filter((s) => s.questions.length).length,
      sectionNumber,
      totalQuestions: this.totalQuestions,
      passingPercent:
        this.testObject.passing && this.testObject.passing.passingPercent
          ? this.testObject.passing.passingPercent
          : 'n/a',
      passingSentence: Tester.getSectionPassingSentence(
        this.testObject,
        this.test.sections.indexOf(section),
        this.userData.accommodation.accuracy,
      ),
    };

    Object.keys(replaceables).forEach((needle) => {
      const re = new RegExp(`{{${needle}}}`, 'g');
      instructions = instructions.replace(re, replaceables[needle]);
    });
    return instructions;
  }

  private createDiv(options?, parentDiv?: HTMLDivElement): HTMLDivElement {
    const styleOptions = {};
    if (typeof options !== 'undefined') {
      utils.merge(styleOptions, options);
    }
    const div = document.createElement('div');
    utils.merge(div.style, styleOptions);
    if (typeof parentDiv === 'undefined') {
      this.displayDiv.appendChild(div);
    } else {
      parentDiv.appendChild(div);
    }
    return div;
  }

  static createButton(label: string, callback: Function): HTMLButtonElement {
    const button = document.createElement('button');
    const text = document.createTextNode(label);
    button.appendChild(text);
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');
    button.appendChild(text);

    button.style.fontSize = '1.5em';

    // A touch produces BOTH a `touchstart` and a synthesized `click`. Handle
    // touchstart for responsiveness, then swallow the click it synthesizes so a
    // single tap invokes the callback exactly once. Genuine mouse/keyboard
    // interaction (click with no preceding touchstart) still fires normally.
    let handledByTouch = false;
    button.addEventListener('touchstart', (evt) => {
      evt.preventDefault();
      handledByTouch = true;
      callback();
    });
    button.addEventListener('click', (evt) => {
      evt.preventDefault();
      if (handledByTouch) {
        handledByTouch = false;
        return;
      }
      callback();
    });
    return button;
  }

  private startTest() {
    // Start the test:
    this.infoDiv.style.display = 'none';
    this.infoDiv.classList.add("info-div");
    this.statsDiv.style.display = 'block';
    this.questionDiv.style.display = 'block';
    // Start the test timer, if one exists.
    if (this.testTimer) {
      this.testTimer.start();
      this.testTimer.onTimeout(() => {
        this.forceStopTest();
      });
    }

    // Setup empty stats for each section:
    this.test.sections.forEach((section, index) =>
      this.newSectionStats(section, index, this.userData?.accommodation?.accuracy),
    );

    this.startSection(0);
  }

  private startSection(sectionNumber: number) {

    const hasMultipleSections =
      this.test.sections.filter((s) => s.questions.length > 0 && !s.options.disabled).length > 1;
    const section = this.test.sections[sectionNumber];
    if (section.questions.length === 0) {
      this.stopSection();
      return;
    }
    this.totalSectionQuestions = section.questions.length;
    this.sectionQuestionNumber = 0;
    if (hasMultipleSections) {
      this.sectionQuestionsDiv.innerHTML = `${'Section question 1 of '}${
        this.totalSectionQuestions
      }`;
    } else {
      this.sectionQuestionsDiv.innerHTML = '';
    }

    // Prepare section timer, if one exists
    if (section.options.timeLimit) {
      this.sectionTimerDiv.innerHTML = `${
        hasMultipleSections ? 'Section ' : ''
      }Time Remaining: <span id="sectionTimerSpan"></span>`;
      const span = document.getElementById('sectionTimerSpan');
      section.timer = new CountdownTimer(span);
      section.timer.set(section.options.timeLimit, this.userData.accommodation.time);
    } else {
      this.sectionTimerDiv.innerHTML = '';
      this.sectionTimerDiv.style.display = 'none';
    }

    // Show section instructions, if any exist.
    if (section.options.instructions) {
      this.infoDiv.style.display = 'block';
      const h2 = document.createElement('h2');
      h2.textContent = 'Section Instructions';
      h2.classList.add('sr-only');
      this.infoDiv.innerHTML = '';
      this.infoDiv.appendChild(h2);

      this.infoDiv.innerHTML += this.processSectionInstructions(
        section,
        section.options.instructions,
      );

      const button = Tester.createButton(
        `Start${hasMultipleSections ? ' Section' : ''}`,
        this.startSectionQuestions.bind(this),
      );
      this.infoDiv.appendChild(button);
    } else {
      this.startSectionQuestions();
    }
  }

  private startSectionQuestions(): void {
    this.infoDiv.style.display = 'none';
    this.infoDiv.innerHTML = '';
    this.questionDiv.style.display = 'block';
    this.removeH2s();
    const h2 = document.createElement('h2');
    h2.textContent = 'Question Content';
    h2.classList.add('sr-only');
    this.displayDiv.insertBefore(h2, this.questionDiv);

    const section = this.test.sections[this.sectionNumber];
    // Start the timer, if it exists:
    if (section.timer) {
      section.timer.start();
      section.timer.onTimeout(this.stopSection, this);
    }

    this.questionPromise = this.displayQuestion();

    // For each question:
    // Show each question
    // Start the questionStopwatch
    // Save the answers and go on to the next question
  }

  /**
   *
   * @param disableAudio When force submitting, don't play aural examples.
   */
  private displayQuestion(disableAudio = false): Promise<any> {
    const section = this.test.sections[this.sectionNumber];
    const question = section.questions[this.sectionQuestionNumber];
    // log('Displaying question:');
    // log(question);
    this.totalSectionQuestions = section.questions.length;
    // Show each question
    if (
      this.sectionQuestionNumber === 0 ||
      !utils.equals(question.options, this.previousQuestion.options) ||
      question.type !== this.previousQuestion.type
    ) {
      if (this.interactive) this.interactive.exit();
      utils.clearResize();

      delete this.interactive;
      this.questionDiv.innerHTML = '';

      if (
        typeof window !== 'undefined' &&
        (window as any).Rollbar &&
        (window as any).Rollbar.captureEvent
      ) {
        (window as any).Rollbar.captureEvent(
          {
            type: 'mid-checkpoint interactive launch',
            interactiveName: question.type,
            options: question.options,
            question: question.question,
          },
          'info',
        );
      }

      // log(`new ${ question.type }`);
      this.interactive = new Interaction[question.type](
        this.questionDiv,
        question.options,
        this.userData,
      );
      this.interactive.addAnswerCallback(this.nextQuestion, this);
      this.interactive.resize();
    }
    this.questionStartTime = new Date().getTime() - 0;
    const questionPromise = this.interactive.loadingPromise.then(() =>
      this.interactive.newCheckpointQuestion(question.question, disableAudio),
    );
    this.interactive.resize();

    this.previousQuestion = question;

    // Update Stats:
    const currentQuestion = this.questionNumber + 1;
    const currentSectionQuestion = this.sectionQuestionNumber + 1;
    this.totalQuestionsDiv.innerHTML = `Question ${currentQuestion} out of ${this.totalQuestions} total`;
    const hasMultipleSections =
      this.test.sections.filter((s) => s.questions.length > 0 && !s.options.disabled).length > 1;

    if (hasMultipleSections) {
      this.sectionQuestionsDiv.innerHTML = `Section question ${currentSectionQuestion} of ${this.totalSectionQuestions}`;
    }

    // Start the questionStopwatch
    this.questionStartTime = new Date().getTime();
    return questionPromise instanceof Promise ? questionPromise : Promise.resolve();
  }

  private nextQuestion(answerAndData: AnswerAndData): void {
    // Save answer and data:
    this.saveAnswerAndData(answerAndData);

    // Go to the next question in a section:
    this.sectionQuestionNumber++;
    this.questionNumber++;
    if (this.sectionQuestionNumber >= this.totalSectionQuestions) {
      this.stopSection();
    } else {
      this.displayQuestion();
    }
  }

  /**
   * @typedef {Object} AnswerAndDataObject
   * @property {string[]|number[]} skills ids
   * @property {number} rightAnswers
   * @property {number} wrongAnswers
   */

  /**
   *
   * @param {AnswerAndDataObject} answerAndData
   * @param {number} totalTime milliseconds
   */
  private progressTrack(answerAndData: AnswerAndData, totalTime: number): void {
    if (totalTime === undefined) {
      throw new Error('total time may not be undefined!');
    }
    if (!answerAndData.skills) return;
    const skills = utils.isArray(answerAndData.skills)
      ? answerAndData.skills
      : [answerAndData.skills];
    skills.forEach((skill) => {
      const skillObj = Tester.makeSkillProgressObj(skill, answerAndData, totalTime);
      this.updateSkillProgress(skillObj);
    });
  }

  /**
   * @typedef {Object} SkillObject
   * @property {number} skill int id
   * @property {number} averageTime ms
   * @property {number} totalTime ms
   * @property {number} percent 0 - 100
   * @property {number} questions number of questions
   * @property {number} rightAnswers int
   * @property {number} wrongAnswers int
   */

  /**
   * Updates the old skill object with a new one
   * @param {SkillObject} newSkillObj
   */
  private updateSkillProgress(newSkillObj: UPracticeData): void {
    /** @type {SkillObject} */
    const skillObj = this.skillDataObjects.find((sObj) => sObj.skill === newSkillObj.skill);
    if (!skillObj) {
      this.skillDataObjects.push(newSkillObj);
      return;
    }

    const reAverage = (prop): void => {
      skillObj[prop] = utils.newAverage(
        skillObj[prop],
        skillObj.questions,
        newSkillObj[prop],
        newSkillObj.questions,
      );
    };

    reAverage('averageTime');
    reAverage('percent');
    skillObj.questions += newSkillObj.questions;
    skillObj.rightAnswers += newSkillObj.rightAnswers;
    skillObj.wrongAnswers += newSkillObj.wrongAnswers;
  }

  /**
   * Create a skill object from answerAndData
   * @param {string|number} skillId
   * @param {AnswerAndDataObject} answerAndData
   * @param {number} totalTime in milliseconds
   */
  static makeSkillProgressObj(
    skillId: string | number,
    answerAndData: AnswerAndData,
    totalTime,
  ): UPracticeData {
    const { rightAnswers, wrongAnswers } = answerAndData;
    const totalQuestions = rightAnswers + wrongAnswers;

    const skillObj = Skills.getSkill(skillId);
    const skill = skillObj ? skillObj.id : -1; // for not found.

    return {
      skill,
      averageTime: totalTime / totalQuestions,
      totalTime,
      percent: 100 * (rightAnswers / totalQuestions),
      questions: totalQuestions,
      rightAnswers,
      wrongAnswers,
    };
  }

  private saveAnswerAndData(answerAndData: AnswerAndData, forceSubmit = false): void {
    if (!answerAndData) {
      throw new Error('Answer and Data not provided.');
    }

    if (this.testObject.saveSkillProgress && !forceSubmit) {
      const now = new Date().getTime() - 0;
      const totalTime = now - this.questionStartTime;
      this.progressTrack(answerAndData, totalTime);
    }

    const section = this.test.sections[this.sectionNumber];
    const question = section.questions[this.sectionQuestionNumber];

    // If time is up -- the answer is wrong, even if the student had written it but
    // not yet submitted it.    const weightedRightAnswers =
    const weightedRightAnswers = forceSubmit ? 0 : answerAndData.rightAnswers * question.weight;

    // If time is up -- the answer is wrong, even if the student had written it but
    // not yet submitted it.
    const weightedWrongAnswers = forceSubmit
      ? (answerAndData.rightAnswers + answerAndData.wrongAnswers) * question.weight
      : answerAndData.wrongAnswers * question.weight;

    const weightedAnswerAndData: WeightedAnswerAndData = {
      ...answerAndData,
      questionNumber: this.questionNumber + 1,
      section: this.sectionNumber,
      answerSpeed: new Date().getTime() - this.questionStartTime,
      weightedRightAnswers,
      weightedWrongAnswers,
      weightedTotalQuestions: answerAndData.rightAnswers + answerAndData.wrongAnswers,
      timeRanOut: forceSubmit,
    };

    this.questionStats.push(weightedAnswerAndData);

    this.updateSectionStats(weightedAnswerAndData);
    this.updateOverallStats();

    this.logQuestion({
      answerAndData: weightedAnswerAndData,
      stats: this.stats,
    });

    this.questionOverCallbacks.forEach((callback) => {
      const questionLog = {
        answerAndData: weightedAnswerAndData,
        stats: this.stats,
      };

      callback(questionLog);
    });
  }

  private logQuestionPromises: Promise<any>[] = [];

  private logQuestion(data: { answerAndData: WeightedAnswerAndData; stats: CheckpointStats }) {
    this.logQuestionPromises.push(
      this.userData.userStore.api.logCheckpointAttemptQuestion(this.checkpointAttemptId, data),
    );
  }

  private newSectionStats(section: Section, index: number, accuracyAccommodation = 1): void {
    const passingPercent = Tester.getSectionPassingPercents(this.testObject, index);

    this.stats.sections.push({
      sectionName: section.options.name,
      totalQuestions: section.questions.length,
      rightAnswers: 0,
      weightedRightAnswers: 0,
      wrongAnswers: 0,
      weightedWrongAnswers: 0,
      percentage: 0,
      passingPercent: passingPercent ? passingPercent * accuracyAccommodation : 0,
      completesLessons: section.options.completesLessons || null,
      completesSkills: section.options.completesSkills || null,
    });
  }

  private updateSectionStats(weightedAnswerAndData: WeightedAnswerAndData): void {
    const stats = this.stats.sections[this.sectionNumber];
    if (!stats) throw Error(`Failed to get section stats for section ${this.sectionNumber}`);
    const mustBeNumbers = [
      'weightedRightAnswers',
      'weightedWrongAnswers',
      'rightAnswers',
      'wrongAnswers',
    ];
    const nanProp = mustBeNumbers.find(
      (prop) => utils.typeof(weightedAnswerAndData[prop]) !== 'number',
    );
    if (nanProp) {
      throw new Error(`${nanProp} must be a number, not ${weightedAnswerAndData[nanProp]}!`);
    }

    const questionPercent =
      (weightedAnswerAndData.weightedRightAnswers * 100) /
      (weightedAnswerAndData.weightedWrongAnswers + weightedAnswerAndData.weightedRightAnswers);

    stats.rightAnswers += weightedAnswerAndData.rightAnswers;
    stats.wrongAnswers += weightedAnswerAndData.wrongAnswers;
    stats.weightedRightAnswers += weightedAnswerAndData.weightedRightAnswers;
    stats.weightedWrongAnswers += weightedAnswerAndData.weightedWrongAnswers;

    stats.percentage += questionPercent / stats.totalQuestions;
  }

  private updateOverallStats(): void {
    utils.clearResize();

    let rightAnswers = 0;
    let wrongAnswers = 0;
    let weightedRightAnswers = 0;
    let weightedWrongAnswers = 0;
    let percentage = 0;
    const totalQuestions = this.totalQuestions;

    // Filter any sections with no questions:
    const totalSectionWeights = this.test.sections
      .filter((section) => section.questions.length)
      .reduce((a, b) => b.options.weight + a, 0);

    this.stats.sections.forEach((section, index) => {
      rightAnswers += section.rightAnswers;
      wrongAnswers += section.wrongAnswers;
      weightedRightAnswers += section.weightedRightAnswers;
      weightedWrongAnswers += section.weightedWrongAnswers;

      const sectionWeight = this.test.sections[index].options.weight;
      percentage += (section.percentage * sectionWeight) / totalSectionWeights;
    });

    this.stats.overall = {
      rightAnswers,
      wrongAnswers,
      weightedRightAnswers,
      weightedWrongAnswers,
      weightedTotalQuestions: weightedRightAnswers + weightedWrongAnswers,
      percentage,
      totalQuestions,
    };
    this.stats.attemptStatus = this.isComplete() ? 'complete' : 'inProgress';
    this.stats.uuid = utils.uuid;
  }

  /**
   * Checks on answer log whether the test will be over as a result of this
   * question.
   */
  isComplete(): boolean {
    // Runs before these are incremented, so we need to add one:
    const isLastQuestion = this.sectionQuestionNumber + 1 >= this.totalSectionQuestions;
    // const isLastSection = this.sectionNumber + 1 >= this.test.sections.length;
    const isLastSection = !this.test.sections.find((section, index) => {
      const hasQuestions = !!section.questions.length;
      const isLaterSection = index > this.sectionNumber;
      return hasQuestions && isLaterSection;
    });
    log('isComplete', isLastSection && isLastQuestion);
    return isLastQuestion && isLastSection;
  }

  private removeH2s(div = this.displayDiv) {
    // Get rid of the question contents accessible h2
    const h2s = div.querySelectorAll('h2.sr-only');
    Array.from(h2s).forEach(h2 => div.removeChild(h2));
  }

  stopSection(): void {
    // Once destroyed, a zombie timer callback must not force-submit anything.
    if (this.destroyed) {
      return;
    }

    if (this.interactive) this.interactive.exit();
    utils.clearResize();


    // Stop the timer. Guard the access: sectionNumber may already have advanced
    // past the last section when a stale timer callback arrives.
    const currentSection = this.test.sections[this.sectionNumber];
    if (currentSection?.timer) {
      currentSection.timer.stop();
    }

    // Otherwise, this was called by a timeout, so we
    // force submission of all remaining responses:
    const sectionOverPromise =
      this.sectionQuestionNumber < this.totalSectionQuestions
        ? this.forceSubmitSection()
        : Promise.resolve();

    sectionOverPromise.then(() => {
      // If there are more sections, prepare them.
      // Otherwise, display Results.
      this.sectionNumber++;
      this.questionDiv.style.display = 'none';
      if (this.sectionNumber < this.test.sections.length) {
        this.startSection(this.sectionNumber);
        this.questionDiv.style.visibility = 'visible';
      } else {
        if (this.testTimer) this.testTimer.stop();
        this.endTest();
      }
    });
  }

  private forceSubmitSection(): Promise<any> {
    this.questionDiv.style.visibility = 'hidden';

    // Submit the current question:
    const saveData = (): Promise<void> =>
      this.questionPromise.then(() => {
        const answerAndData = this.interactive.getAnswerAndData();
        log('forcing answer and data:');
        log(answerAndData);
        log('from:');
        log(this.interactive);
        this.saveAnswerAndData(answerAndData, true);
        this.sectionQuestionNumber++;
        this.questionNumber++;
      });

    const nextQuestion = (): Promise<void> =>
      this.sectionQuestionNumber < this.totalSectionQuestions
        ? this.displayQuestion().then(saveData).then(nextQuestion)
        : Promise.resolve();

    return saveData().then(nextQuestion);
  }

  /**
   * Tear the tester down when its host component unmounts. Stops the test timer
   * and every section timer (so no orphaned CountdownTimer interval can survive
   * to force-submit answers to an abandoned attempt), exits any live
   * interactive, and flips the `destroyed` guard so later timer callbacks
   * (e.g. stopSection) become no-ops.
   */
  destroy(): void {
    this.destroyed = true;

    if (this.testTimer) {
      this.testTimer.destroy();
    }

    this.test.sections.forEach((section) => {
      if (section.timer) {
        section.timer.destroy();
      }
    });

    if (this.interactive) {
      this.interactive.exit();
    }
  }

  // ### NOT YET IMPLEMENTED
  forceStopTest() {
    const section = this.test.sections[this.sectionNumber];
    // Stop the test timer, if its' running:
    if (this.testTimer) {
      this.testTimer.stop();
    }

    // Stop the timer, if it exists:
    if (section?.timer) {
      section.timer.stop();
    }

    // And exit any present interactive
    if (this.interactive) {
      this.interactive.exit();
    }

    /*
    //Buggy, buggy code !!!
    this.infoDiv.style.visibility = "hidden";
    this.questionDiv.style.visibility = "hidden";
    this.forceSubmitSection();
    while(this.section < this.test.sections.length) {
      this.startSection();
      this.startSectionQuestions();
      this.forceSubmitSection();
      this.section++;
    }
    this.infoDiv.style.visibility = "visible";
    this.questionDiv.style.visibility = "visible";

    // Test time ran out -- force submission of each remaining
    // section.
*/
  }

  addGameOverCallback(callback: Tester['gameOverCallbacks'][number], thisArg?: any): void {
    if (typeof thisArg !== 'undefined') {
      this.gameOverCallbacks.push(callback.bind(thisArg));
    } else this.gameOverCallbacks.push(callback);
  }

  addQuestionOverCallback(callback: QuestionOverCallback, thisArg?): void {
    if (typeof thisArg !== 'undefined') {
      this.questionOverCallbacks.push(callback.bind(thisArg));
    } else this.questionOverCallbacks.push(callback);
  }

  private async endTest(): Promise<void> {
    // If we have practiceData to save, do it -- we don't really care
    // whether it succeeds or not:
    this.userData.userStore.api.sendProgressData({ practiceData: this.skillDataObjects });

    // Do a final stats tally:
    this.updateOverallStats();
    //      log(this.stats);

    let questionsSaveStatus: 'saving' | 'done' | 'errored' = 'saving';
    let finalResultsSaveStatus: 'saving' | 'done' | 'errored' = 'saving';

    const updateInnerHtml = () => {
      if (questionsSaveStatus === 'saving' || finalResultsSaveStatus === 'saving') {
        this.displayDiv.innerHTML = `
          <h3>Saving Test</h3>
          <p>We are saving your test, please hold tight for a few moments.</p>
        `;
      } else if (questionsSaveStatus === 'errored' || finalResultsSaveStatus === 'errored') {
        this.displayDiv.innerHTML = `
          <h3>Error</h3>
          <p>There were problems saving your test.</p>
        `;
      } else {
        // The test was saved correctly, nothing to show here!
        this.displayDiv.innerHTML = '';
        return;
      }
      switch (questionsSaveStatus) {
        case 'saving':
          this.displayDiv.innerHTML += '<p>Saving test questions...</p>';
          break;
        case 'done':
          this.displayDiv.innerHTML += '<p>Test questions saved.</p>';
          break;
        case 'errored':
          this.displayDiv.innerHTML += `
            <p>
              <span class="error">Error:</span>
              We were unabled to save one or more test questions. You may retry using the
              retry button(s) above.
              ${
                finalResultsSaveStatus === 'saving'
                  ? 'We may still be able to save the overall score.'
                  : ''
              }
              ${finalResultsSaveStatus === 'done' ? 'We were able to save the overall score.' : ''}
            </p>
          `;
      }
      switch (finalResultsSaveStatus) {
        case 'saving':
          this.displayDiv.innerHTML += '<p>Saving overall score...</p>';
          break;
        case 'errored':
          this.displayDiv.innerHTML +=
            '<p class="error">Unable to save the overall test score. Use the retry button above to try again.</p>';
          break;
      }
      if (
        finalResultsSaveStatus !== 'saving' &&
        questionsSaveStatus !== 'saving' &&
        (finalResultsSaveStatus === 'errored' || questionsSaveStatus === 'errored')
      ) {
        this.displayDiv.innerHTML += `
          <p>This message will no longer update, but clicking
          retry on any error messags above may still allow the
          test attempt to be saved.</p>
        `;
      }
    };

    const saveQuestionsPromise = Promise.all(this.logQuestionPromises)
      .then(() => {
        questionsSaveStatus = 'done';
        updateInnerHtml();
        return true;
      })
      .catch((err) => {
        errorProvider.error(err);
        questionsSaveStatus = 'errored';
        updateInnerHtml();
        return false;
      });

    const stats = this.stats;
    stats.attemptStatus = 'complete';

    const saveFinalStatsPromise = this.userData.userStore.api
      .finishCheckpointAttempt(this.checkpointAttemptId, stats)
      .then((resultsPossiblyWithoutQuestions) => {
        finalResultsSaveStatus = 'done';
        updateInnerHtml();
        return resultsPossiblyWithoutQuestions;
      })
      .catch((err) => {
        errorProvider.error(err);
        return saveQuestionsPromise.then((questionsOk) => {
          finalResultsSaveStatus = questionsOk ? 'done' : 'errored';
          updateInnerHtml();
          return questionsOk;
        });
      });

    const [questionsSaved, finalResultsSaved] = await Promise.all([
      saveQuestionsPromise,
      saveFinalStatsPromise,
    ]);

    // We never end the checkpoint if it's not saved.
    if (questionsSaved && finalResultsSaved) {
      this.userData.userStore.api
        .loadCheckpointAttempt(this.checkpointAttemptId)
        .then((results) => {
          this.gameOverCallbacks.forEach((callback) => {
            callback(results.record as CheckpointResults);
          });
        })
        .catch(() => {
          this.gameOverCallbacks.forEach((callback) => {
            callback();
          });
        });
    }
  }
}

function processTest(testObject: CheckpointTest | LessonCheckpointTest | MergedCustomCheckpoint): {
  options: CheckpointOptions;
  sections: Section[];
} {
  // log('processing test', testObject);
  const options = testObject.options;
  const sections = testObject.sections.map(
    (section, sectionIndex): Section => ({
      options: section.options,
      questions: processSection(section, sectionIndex),
    }),
  );

  return {
    options,
    sections,
  };
}

function processSection(sectionObject: CheckpointSection, sectionIndex?: number): Question[] {
  log(
    `processing section ${sectionIndex}: ${sectionObject.options.name}.
    (questionGroups Count: ${sectionObject.questionGroups.length}).
    Disabled: ${sectionObject.options.disabled}`,
    sectionObject,
  );
  log({
    sectionObject,
  });
  if (sectionObject.options.disabled) return [];

  const totalGroups = sectionObject.questionGroups.filter(
    (group) => !(group.options && group.options.disabled),
  ).length;
  const onlyOneGroup = totalGroups === 1;

  const sectionQuestions = sectionObject.questionGroups
    .map((group, index) => {
      log(`Section ${sectionObject.options.name}, Group ${index}: ${group.options?.name}`);
      return processQuestionGroup(group, onlyOneGroup);
    })
    .reduce((accum, questions) => accum.concat(questions), []);

  // Sort by group (do nothing), type, fully random.
  const sortTypes = {
    group(questionObjects: Question[]): Question[] {
      // do nothing -- just return, they're already by group:

      return questionObjects;
    },
    type(questionObjects: Question[]): Question[] {
      console.log('sorting by type...', questionObjects);
      console.log(questionObjects.map((qo) => qo.type));
      return utils.shuffle(questionObjects).sort((a, b) => (a.typeUuid < b.typeUuid ? -1 : 1));
    },
    typeIgnoreOptions(questionObjects: Question[]): Question[] {
      utils.shuffle(questionObjects);
      return questionObjects.sort((a, b) => (a.type < b.type ? -1 : 1));
    },
    random(questionObjects: Question[]): Question[] {
      utils.shuffle(questionObjects);
      return questionObjects;
    },
  };
  if (typeof sectionObject.options.sortOrder !== 'undefined') {
    const isReversed = sectionObject.options.sortOrder.includes('Dsc');
    const sortOrder = sectionObject.options.sortOrder.replace('Dsc', '');

    if (sortOrder in sortTypes) {
      sortTypes[sortOrder](sectionQuestions);
    }
    if (isReversed) sectionQuestions.reverse();
  }

  console.log('sorted', sectionQuestions);
  return sectionQuestions;
}

/**
 * If a user has set a different number of questions for the group, we adjust
 * the number of questions required of each questionBank in the group accordingly
 * @param questionGroup
 */
function overrideQuestionsPerBank(questionGroup: QuestionGroup): QuestionGroup {
  log('\n\nQuestion Group: ', questionGroup?.options?.name, questionGroup);
  const defaultTotalQuestions = questionGroup.questionBanks.reduce(
    (totalQs, bank) => bank.numberOfQuestions + totalQs,
    0,
  );
  // If the number of questions per group is not set, return unaltered:
  if (
    !questionGroup.options ||
    questionGroup.options.numberOfQuestions === undefined ||
    questionGroup.options.numberOfQuestions === null ||
    questionGroup.options.numberOfQuestions === defaultTotalQuestions
  ) {
    // log('nothing to see here...');
    return questionGroup;
  }

  // Otherwise, figure out how many questions we're delivering:
  const maxTotalQuestions = questionGroup.questionBanks.reduce(
    (totalQs, bank) => (bank.maxQuestions || bank.questions.length) + totalQs,
    0,
  );

  // Let's be sure that there haven't accidentally been too many questions set:
  const numberOfQuestions =
    questionGroup.options.numberOfQuestions > maxTotalQuestions
      ? maxTotalQuestions
      : questionGroup.options.numberOfQuestions;

  // log(
  //   `${
  //     questionGroup.options.name
  //   } max questions: ${ maxTotalQuestions }, number of questions: ${ numberOfQuestions }`
  // );

  // Ok, we've gotta make some changes:
  questionGroup = utils.clone(questionGroup, true);

  interface QuestionBankHolder {
    questionBank: QuestionBank;
    numberOfQuestions: number;
    maxQuestions: number;
    idealNumberOfQuestions: number;
  }

  const ratio = numberOfQuestions / defaultTotalQuestions;
  const bankHolders = questionGroup.questionBanks.map((bank) => {
    const maxBankQuestions = bank.maxQuestions || bank.questions.length;
    return {
      questionBank: bank,
      numberOfQuestions: Math.min(~~(bank.numberOfQuestions * ratio), maxBankQuestions),
      maxQuestions: maxBankQuestions,
      idealNumberOfQuestions: bank.numberOfQuestions * ratio,
    };
  });
  // log(
  //   'bankHolders numberOfQuestions first pass: ',
  //   bankHolders.map(b => b.numberOfQuestions).join(', ')
  // );

  const tallyQuestions = (): number =>
    bankHolders.reduce((totalQ, bankHolder) => totalQ + bankHolder.numberOfQuestions, 0);

  const updateQuestions = (): void => {
    bankHolders.forEach((bankHolder) => {
      bankHolder.questionBank.numberOfQuestions = bankHolder.numberOfQuestions;
    });
  };

  // Maybe we lucked out and there were no fractions...
  if (tallyQuestions() === numberOfQuestions) {
    updateQuestions();
    // log('first pass, right number of questions');
    // log('bankHolders', bankHolders);
    // log('updated questionGroup', questionGroup);
    return questionGroup;
  }

  const distanceFromIdeal = (bankHolder: QuestionBankHolder): number =>
    bankHolder.idealNumberOfQuestions - bankHolder.numberOfQuestions;

  const bankHolderSorter = (holder1: QuestionBankHolder, holder2: QuestionBankHolder): number => {
    if (
      holder1.numberOfQuestions === holder1.maxQuestions &&
      holder2.numberOfQuestions === holder2.maxQuestions
    ) {
      return 0;
    }
    if (holder1.numberOfQuestions === holder1.maxQuestions) return 1;
    if (holder2.numberOfQuestions === holder2.maxQuestions) return -1;
    return distanceFromIdeal(holder2) - distanceFromIdeal(holder1);
  };
  // Or, maybe not...
  let questionsRemaining = numberOfQuestions - tallyQuestions();
  // log('questionsRemaining: ', questionsRemaining);

  /**
   * Add another question to a question bank.
   * @param bankHolder
   */
  const questionConsumer = (bankHolder: {
    questionBank: QuestionBank;
    numberOfQuestions: number;
    maxQuestions: number;
    idealNumberOfQuestions: number;
  }): void => {
    if (bankHolder.numberOfQuestions >= bankHolder.maxQuestions) return;
    if (questionsRemaining > 0) {
      bankHolder.numberOfQuestions++;
    }
    questionsRemaining--;
  };

  let safetyLoop = 5;
  while (questionsRemaining > 0 && safetyLoop-- > 0) {
    bankHolders.sort(bankHolderSorter).forEach(questionConsumer);
  }
  updateQuestions();
  return questionGroup;
}

function processQuestionGroup(questionGroup: QuestionGroup, isOnlyGroup: boolean): Question[] {
  log('processQuestionGroup', questionGroup);
  const qg = new QuestionGenerator();
  if (questionGroup.options && questionGroup.options.disabled) return [];
  questionGroup = overrideQuestionsPerBank(questionGroup);
  const { questionBanks } = questionGroup;
  // log(`questionGroup:`, questionGroup);
  const questionTypes = questionGroup.questionTypes.filter(
    (type) => !isOnlyGroup || !type.excludeIfOnlyQuestionGroup,
  );
  const questions = questionBanks
    .map((questionBank) => {
      const questionBankQuestions = questionBank.questions;
      // log('Question bank requestion num questions:', questionBank.numberOfQuestions);
      // log('groupQuestions', groupQuestions);

      const groupQuestions =
        questionBankQuestions.length > 1
          ? qg.rejectDuplicates(questionBank.questions, questionBank.numberOfQuestions)
          : Array(questionBank.numberOfQuestions).fill(questionBankQuestions[0]);
      // log('generated questions.length', questions.length);
      return groupQuestions;
      // return utils
      //   .shuffle(questionBank.questions.slice(0))
      //   .slice(0, questionBank.numberOfQuestions);
    })
    .reduce((accum, qs) => accum.concat(qs));
  utils.shuffle(questions);

  const proportionalTotal = questionTypes.reduce(
    (accum, questionType) => accum + (questionType.questionWeighting || 1),
    0,
  );
  const questionTypesClone: QuestionType[] = utils.clone(questionTypes, true);
  const questionTypesWithQsNeeded = questionTypesClone.map((type) => ({
    qsNeeded: ((type.questionWeighting || 1) * questions.length) / proportionalTotal,
    typeUuid: `${utils.uuid}. ${type.questionType}`,
    ...type,
  }));
  const nextType = () => {
    questionTypesWithQsNeeded.sort((a, b) => b.qsNeeded - a.qsNeeded);
    questionTypesWithQsNeeded[0].qsNeeded--;
    return questionTypesWithQsNeeded[0];
  };
  // log('questionGroup.questions.length', questions.length);
  return questions.map((question) => {
    const type = nextType();
    return {
      type: type.questionType,
      typeUuid: type.typeUuid,
      weight: type.questionWeighting || 1,
      options: { testMode: true, ...(type.options || {}) },
      question,
    };
  });
}

function getPassingWithAccommodation(
  passing: Passing,
  accommodation: Pick<Accommodation, 'accuracy' | 'time'>,
) {
  const accuracyAccommodation = 'accuracy' in accommodation ? accommodation.accuracy : 1;
  // There was a bug in the frontend for a time that caused passing.passingPercent to be
  // saved as a string. So... we do this:
  const isNumeric = !Number.isNaN(Number(passing.passingPercent));

  const passingPercent = isNumeric ? accuracyAccommodation * passing.passingPercent : 0;
  const type: Passing['type'] = passing.type || null;
  return {
    type,
    passingPercent,
  };
}
